import React, { useState, useEffect, useCallback } from 'react';

import { Link } from 'react-router-dom';

import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Alert from 'react-bootstrap/Alert';
import Col from 'react-bootstrap/Col';

import * as api from 'api/costing';

const CostingSummary = React.memo(({ match }) => {
  const {
    params: { country },
    url
  } = match;

  const [error, setError] = useState(null);
  const [versions, setVersions] = useState([]);
  const [originalCostingVersion, setOriginalCostingVersion] = useState('');
  const [costingVersion, setCostingVersion] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);

    async function getCostingVersions() {
      try {
        const costingVersions = await api.getCostingVersions(country);
        const { id: current } = await api.getCurrentCostingVersion(country);

        setOriginalCostingVersion(current);
        setCostingVersion(current);
        setVersions(costingVersions);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    }

    getCostingVersions();
  }, [country]);

  const handleSelectChange = useCallback(event => {
    setCostingVersion(parseInt(event.target.value, 10));
  }, []);

  const handleCancelClick = useCallback(() => {
    setCostingVersion(originalCostingVersion);
  }, [originalCostingVersion]);

  const handleSaveClick = useCallback(async () => {
    setSaving(true);
    try {
      await api.putCostingVersion(costingVersion);
      setOriginalCostingVersion(parseInt(costingVersion, 10));
      setSaving(false);
    } catch (error) {
      setError(error);
    }
  }, [costingVersion]);

  const allowEdition = !!costingVersion && originalCostingVersion !== costingVersion;

  const { tag: currentCostingVersion } = versions.find(v => v.id === originalCostingVersion) || {};

  return (
    <div>
      {!!error && <Alert variant="danger">We got unexpected error</Alert>}
      {!!loading && (
        <div className="mt-4 d-flex justify-content-center">
          <Spinner animation="border" />
        </div>
      )}
      {!loading && (
        <>
          <Form>
            <p>
              Current cost version: <strong>{currentCostingVersion}</strong>
            </p>
            <Form.Group as={Row}>
              <Form.Label column sm={3}>
                Select the new cost version
              </Form.Label>
              <Col sm={3}>
                <Form.Control as="select" value={costingVersion} onChange={handleSelectChange}>
                  {versions.map(version => (
                    <option key={version.id} value={version.id}>
                      {version.tag}
                    </option>
                  ))}
                </Form.Control>
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="d-flex justify-content-end">
              {!!allowEdition && (
                <Col sm={6}>
                  <Button
                    variant="light"
                    disabled={!allowEdition}
                    className="mr-2"
                    onClick={handleCancelClick}
                  >
                    Cancel
                  </Button>
                  <Button disabled={!allowEdition || saving} onClick={handleSaveClick}>
                    {!!saving && <Spinner animation="border" variant="light" size="sm" />}
                    {!saving && <>Apply new cost version</>}
                  </Button>
                </Col>
              )}
            </Form.Group>
            <div className="mt-5">
              <Link to={`${url}/create`}>
                <Button variant="outline-primary">Create new one</Button>
              </Link>
            </div>
          </Form>
        </>
      )}
    </div>
  );
});

export default CostingSummary;
